import React from 'react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { store } from '../../redux/oldStore';
import { AdvancedTypes, Product, ProductsResponse } from '../../utils/CommonTypes/StoreTypes';
import { ServerResponse } from '../../utils/Server';
import { formatMoney, FormatPhoneNumber } from '../../utils/Tools';
import Icon, { IconType } from '../Icon/Icon';
import HousingPackageFormFields from './HousingPackageFormFields';

interface ProductInModalProps {
  product: Product;

  childProducts?: Product[];
  handleProductsResponse: (stuff: ProductsResponse) => void;
  handleProductClose: (open?: boolean) => void;
  userId: string | null;
  orderId?: string;
}

const ProductInModal = (props: ProductInModalProps) => {
  const [item, setItem] = useState<Product>(props.product);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [anyFormData, setAnyFormData] = useState<boolean>(false);
  const [size, setSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [logoUploading, setLogoUploading] = useState(false);

  const handleAddToCartConfirm = () => {
    let submitObj = {
      productId: item.Id,
      quantity,
      size,
      userId: props.userId,
      custom: '',
      orderId: props.orderId,
    };
    if (anyFormData) {
      submitObj.custom = JSON.stringify(formData);
    }
    let errors: string[] = [];
    if (item.AdvancedType === AdvancedTypes.Normal && item.Sizes && item.Sizes.length > 0 && item.Sizes[0] && !size) {
      toast.warning(`You must select a size.`);
      return;
    } else if (item.AdvancedType === AdvancedTypes.HandlingLabel) {
      if (!formData['NumberOfPackages'] || Number(formData['NumberOfPackages']) <= 0) errors.push(`Please set the number of packages you'll be sending.`);
      if (!formData['SendersName']) errors.push(`Please set the senders name.`);
      if (!formData['AddressLine1']) errors.push(`Address line 1 is required.`);
      if (!formData['AddressLine2']) errors.push(`Address line 2 is required.`);
      //if(!formData['AddressLine3']) errors.push(``);
      //if(!formData['AddressLine4']) errors.push(``);
      submitObj.quantity = Number(formData['NumberOfPackages']);
    } else if (item.AdvancedType === AdvancedTypes.HousingPackage) {
      if (!formData[`PackageFor`] || formData[`PackageFor`] === '.') errors.push(`Package is for is required.`);
      if (!formData[`OccupantsFirstName`]) errors.push(`Occupants first name is required. `);
      if (!formData[`OccupantsLastName`]) errors.push(`Occupants last name is required.`);
      if (!formData[`OccupantsAge`] || formData[`OccupantsAge`] === '.') errors.push(`Age is required`);
      if (!formData[`OccupantsGender`] || formData[`OccupantsGender`] === '.') errors.push(`Gender is required`);
      //if (!formData[`ChaperonesName`]) errors.push(`Chaperones name is required`);
      //if (!formData[`RoommatesName`]) errors.push(``);
      //if (!formData[`Needs`]) errors.push(``);
      submitObj.quantity = 1;
    } else if (item.AdvancedType === AdvancedTypes.AdditionalAttendee) {
      submitObj.quantity = 1;
      if (!formData['Relationship'] || formData['Relationship'] === '.') errors.push(`Relationship is required`);
      if (!formData['AgeRange'] || formData['AgeRange'] === '.') errors.push(`Age range is required`);
      if (!formData['FirstName'] || false) errors.push(`First name is required`);
      if (!formData['LastName'] || false) errors.push(`Last name is required`);
    } else if (item.AdvancedType === AdvancedTypes.SponsorshipTier) {
      submitObj.quantity = 1;
      if (!formData['OrgName']) errors.push(`Organization name is required`);
    }

    if (errors && errors.length > 0) {
      toast.warning(errors.join(', '));
      return;
    }

    store.server.postApi<ServerResponse<ProductsResponse>>('../Store/AddToCart', submitObj).then((res) => {
      if (!res.Success) {
        toast.error(res.Message || 'Could not add to cart.', { autoClose: false });
        return;
      }
      toast.success('Added to cart!');
      props.handleProductsResponse(res.Value);
      props.handleProductClose();
    });
  };

  const handleFormChange = (key: string, value: string) => {
    setFormData({ ...formData, [key]: value });
    setAnyFormData(true);
  };

  const inputsByAdvancedType = (type: AdvancedTypes) => {
    switch (type) {
      case AdvancedTypes.HousingPackage:
        return <HousingPackageFormFields formData={formData} onChange={handleFormChange} />;
      case AdvancedTypes.AdditionalAttendee:
        return (
          <>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="Relationship">
                Relationship to Attendee
              </label>
              <div className="col-sm-6">
                <select className="form-control" name="Relationship" id="Relationship" value={formData?.Relationship ?? '.'} onChange={(e) => handleFormChange('Relationship', e.target.value)}>
                  <option value="."> </option>
                  <option value="ParentGuardian">Parent / Guardian</option>
                  <option value="Sibling">Sibling</option>
                  <option value="FamilyMember">Family Member</option>
                  <option value="Friend">Friend</option>
                  <option value="Chaperone">Chaperone</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="FirstName">
                First Name
              </label>
              <div className="col-sm-6">
                <input className="form-control" type="text" name="FirstName" id="FirstName" value={formData?.FirstName ?? ''} onChange={(e) => handleFormChange('FirstName', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="LastName">
                Last Name
              </label>
              <div className="col-sm-6">
                <input className="form-control" type="text" name="LastName" id="LastName" value={formData?.LastName ?? ''} onChange={(e) => handleFormChange('LastName', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="AgeRange">
                Age Range
              </label>
              <div className="col-sm-6">
                <select className="form-control" name="AgeRange" id="AgeRange" value={formData?.AgeRange ?? '.'} onChange={(e) => handleFormChange('AgeRange', e.target.value)}>
                  <option value="."> </option>
                  <option value="4-12">4 - 12 Years Old</option>
                  <option value="13+">13 and Older</option>
                </select>
              </div>
            </div>
          </>
        );
      case AdvancedTypes.HandlingLabel:
        return (
          <>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="NumberOfPackages">
                Number of Packages
              </label>
              <div className="col-sm-6">
                <input
                  className="form-control"
                  type="number"
                  name="NumberOfPackages"
                  id="NumberOfPackages"
                  value={formData?.NumberOfPackages ?? ''}
                  onChange={(e) => handleFormChange('NumberOfPackages', e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="SendersName">
                Sender's Name
              </label>
              <div className="col-sm-6">
                <input className="form-control" type="text" name="SendersName" id="SendersName" value={formData?.SendersName ?? ''} onChange={(e) => handleFormChange('SendersName', e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="AddressLine1">
                Return Address
              </label>
              <div className="col-sm-6">
                <input
                  className="form-control"
                  type="text"
                  name="AddressLine1"
                  id="AddressLine1"
                  value={formData?.AddressLine1 ?? ''}
                  onChange={(e) => handleFormChange('AddressLine1', e.target.value)}
                />
                <input
                  className="form-control"
                  type="text"
                  name="AddressLine2"
                  id="AddressLine2"
                  placeholder="address"
                  value={formData?.AddressLine2 ?? ''}
                  onChange={(e) => handleFormChange('AddressLine2', e.target.value)}
                />
                <input
                  className="form-control"
                  type="text"
                  name="AddressLine3"
                  id="AddressLine3"
                  value={formData?.AddressLine3 ?? ''}
                  onChange={(e) => handleFormChange('AddressLine3', e.target.value)}
                />
                <input
                  className="form-control"
                  type="text"
                  name="AddressLine4"
                  id="AddressLine4"
                  value={formData?.AddressLine4 ?? ''}
                  onChange={(e) => handleFormChange('AddressLine4', e.target.value)}
                />
              </div>
            </div>
          </>
        );
      case AdvancedTypes.SponsorshipTier:
        return (
          <>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="OrgName">
                Organization Name
              </label>
              <div className="col-sm-6">
                <input className="form-control" type="text" name="OrgName" id="OrgName" value={formData?.OrgName ?? ''} onChange={(e) => handleFormChange('OrgName', e.target.value)} maxLength={250} />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="Website">
                Website
              </label>
              <div className="col-sm-6">
                <input className="form-control" type="url" name="Website" id="Website" value={formData?.Website ?? ''} onChange={(e) => handleFormChange('Website', e.target.value)} maxLength={1000} />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="LogoFile">
                Logo
              </label>
              <div className="col-sm-6">
                <input
                  className="form-control"
                  type="file"
                  name="LogoFile"
                  id="LogoFile"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) return;
                    setLogoUploading(true);
                    store.server.postApi<ServerResponse<{ url: string }>>('../Store/UploadCartImage', {}, [file]).then((res) => {
                      setLogoUploading(false);
                      if (res.Success && res.Value?.url) {
                        handleFormChange('LogoUrl', res.Value.url);
                        toast.success('Logo uploaded.');
                      } else {
                        toast.error(res.Message || 'Could not upload logo.', { autoClose: false });
                      }
                    });
                  }}
                />
                {logoUploading && <div className="help-block">Uploading…</div>}
                {formData?.LogoUrl && (
                  <div style={{ marginTop: 8 }}>
                    <img src={formData.LogoUrl} alt="Logo preview" style={{ maxHeight: 80 }} />
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="ContactName">
                Contact Name
              </label>
              <div className="col-sm-6">
                <input className="form-control" type="text" name="ContactName" id="ContactName" value={formData?.ContactName ?? ''} onChange={(e) => handleFormChange('ContactName', e.target.value)} maxLength={100} />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="Email">
                Email
              </label>
              <div className="col-sm-6">
                <input className="form-control" type="email" name="Email" id="Email" value={formData?.Email ?? ''} onChange={(e) => handleFormChange('Email', e.target.value)} maxLength={150} />
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="Phone">
                Phone
              </label>
              <div className="col-sm-6">
                <input
                  className="form-control"
                  type="tel"
                  name="Phone"
                  id="Phone"
                  value={formData?.Phone ?? ''}
                  onChange={(e) => handleFormChange('Phone', FormatPhoneNumber(e.target.value))}
                  maxLength={20}
                />
              </div>
            </div>
          </>
        );
      case AdvancedTypes.TeacherRegistrationFee:
        return (
          <>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="TeacherFirstName">
                First Name
              </label>
              <div className="col-sm-6">
                <input
                  className="form-control"
                  type="text"
                  name="TeacherFirstName"
                  id="TeacherFirstName"
                  value={formData?.FirstName ?? ''}
                  onChange={(e) => handleFormChange('FirstName', e.target.value)}
                  maxLength={50}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="TeacherLastName">
                Last Name
              </label>
              <div className="col-sm-6">
                <input
                  className="form-control"
                  type="text"
                  name="TeacherLastName"
                  id="TeacherLastName"
                  value={formData?.LastName ?? ''}
                  onChange={(e) => handleFormChange('LastName', e.target.value)}
                  maxLength={50}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="Email">
                Email
              </label>
              <div className="col-sm-6">
                <input className="form-control" type="email" name="Email" id="Email" value={formData?.Email ?? ''} onChange={(e) => handleFormChange('Email', e.target.value)} maxLength={150} />
              </div>
            </div>

            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="Phone">
                Phone
              </label>
              <div className="col-sm-6">
                <input
                  className="form-control"
                  type="tel"
                  name="Phone"
                  id="Phone"
                  value={formData?.Phone ?? ''}
                  onChange={(e) => handleFormChange('Phone', FormatPhoneNumber(e.target.value))}
                  maxLength={20}
                />
              </div>
            </div>
          </>
        );
      // case AdvancedTypes.Normal:
      default:
        return (
          <>
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="quantity">
                Quantity
              </label>
              <div className="col-sm-6">
                <input className="form-control" type="number" name="quantity" id="quantity" min={0} value={quantity ?? 1} onChange={(e) => setQuantity(+e.target.value)} />
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <div>
      <div className="flex-between">
        {item.ImageUrl ? (
          <div className="store-item-image">
            <img src={item.ImageUrl} alt={item.Name} />
          </div>
        ) : props.product.ImageUrl ? <>
          <div className="store-item-image">
            <img src={props.product.ImageUrl} alt={props.product.Name} />
          </div>
        </> : <></>}
        <h2>
          {item.Name} - {formatMoney.format(item.Cost)}
        </h2>
      </div>
      <div dangerouslySetInnerHTML={{ __html: item.Description ?? '' }}></div>
      <div className="form-horizontal">
        {props.product && (props.product.GroupParentItem === 'Filter') && <>
          <div className="form-group">
            <label className="control-label col-sm-6" htmlFor="Cost">
              {props.product.Name}
            </label>
            <div className="col-sm-6">
              <select className="form-control" name="GroupWith" id="GroupWith" 
              
              onChange={(e) => setItem(props.childProducts?.find(p=>p.Id.toString() === e.target.value) ?? {...item, Id:0})}>
                <option value=".">Select Option</option>
                {props.childProducts && props.childProducts.length > 0 ? (  
                  props.childProducts.map((x) => {
                    return (
                      <option key={x.Id} value={x.Id}>  

                        {x.Name} - {formatMoney.format(x.Cost)}
                      </option>
                    );
                  })) : (
                    <option value=".">No options available</option>
                  )}
              </select>
            </div>
          </div>
        </>}
        {item && (item.GroupParentItem !== 'Filter') && <>
          <div className="form-group">
            <label className="control-label col-sm-6" htmlFor="Cost">
              Cost
            </label>
            <div className="col-sm-6">
              <input
                className="form-control"
                readOnly={true}
                type="text"
                name="Cost"
                id="Cost"
                value={formatMoney.format(item.Cost)}
                // onChange={(e) => handleFormChange('', e.target.value)}
              />
            </div>
          </div>
          {item.Sizes?.length > 1 && (
            <div className="form-group">
              <label className="control-label col-sm-6" htmlFor="size">
                Size
              </label>
              <div className="col-sm-6">
                <select className="form-control" name="size" id="size" value={size} onChange={(e) => setSize(e.target.value)}>
                  <option value=".">Select Size</option>
                  {item.Sizes.map((x) => {
                    return (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          )}
          {inputsByAdvancedType(item.AdvancedType)}
        </>}
      </div>
      {(props.product.GroupParentItem !== 'Filter' || (item.Id > 0 && item.Id !== props.product.Id)) && (
        <div className="form-group">
          <div className="col-sm-6 col-sm-offset-6">
            <button className="btn btn-primary" onClick={handleAddToCartConfirm}>
              <Icon type={IconType.plus} /> Add to Cart
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductInModal;
